import { u } from "./url";
import { showToast } from "./toast";
import { csrfToken } from "./csrf";
/**
 * Composer — グローバルモーダル + インラインフォーム両対応。
 *
 * モーダル: data-composer-trigger='{"mode":"reply","postId":N,"preview":{...}}' クリックで開く。
 * インライン: form.composer-inline.cm を直接送信 (タイムライン上部の投稿欄など)。
 */

interface PreviewPayload {
  handle?: string;
  display_name?: string;
  avatar_url?: string;
  body?: string;
  image_url?: string;
  video_thumbnail?: string;
}

interface ComposerOpts {
  mode: "new" | "reply" | "quote";
  postId?: number;
  preview?: PreviewPayload;
  // 現況届 (genkyo) コンテキスト: ホームの ?genkyo_id=N で開いた時に
  // 「現況届に投稿」バナー + 隠し input genkyo_id を仕込む。
  // PHP post.php の $_GET['genkyo'] 経路と等価。
  genkyoId?: number;
  genkyoTopic?: string;
  // 帯のピルから開くときの付帯情報 (composer_modal.html の cmSetGenkyo がピッカーと同じ 1 行に描く)
  genkyoGenre?: string;
  genkyoHost?: string;
  genkyoRemain?: string;
  genkyoAnswers?: number;
}

interface GenkyoSetter {
  cmSetGenkyo?: (it: { id: number; topic: string; genre: string; host: string; remain: string; ans: number; mori: number }) => void;
  cmClearGenkyo?: () => void;
}

interface UploadResponse { ok: boolean; url?: string; error?: string; }
interface UploadVideoResponse { ok: boolean; url?: string; thumb?: string; error?: string; }

const MAX_IMAGES = 4;

declare global {
  interface Window {
    __cmQueue?: ComposerOpts[];
    openComposerModal?: (opts: ComposerOpts) => void;
    closeComposerModal?: () => void;
  }
}

function $<T extends Element>(root: ParentNode, sel: string): T | null {
  return root.querySelector(sel) as T | null;
}

// localDateTimeValue は Date を <input type="datetime-local"> が受ける
// "YYYY-MM-DDTHH:MM" (ローカル時刻) に変換する。
// toISOString() は UTC を返すため JST 前提のこのサイトでは 9 時間ずれる → 使わない。
function localDateTimeValue(d: Date): string {
  const p = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
}

// formatScheduleLabel は "2026-07-30T09:00" を "7/30 09:00 に投稿" にする。
// 文字列のまま組み立てる (Date を通さないので TZ 解釈のずれが入り込まない)。
function formatScheduleLabel(value: string): string {
  const m = /^\d{4}-(\d{2})-(\d{2})T(\d{2}:\d{2})/.exec(value);
  if (!m) return "予約";
  return `${Number(m[1])}/${Number(m[2])} ${m[3]} に投稿`;
}

// imageFilesFromClipboard はクリップボードのデータから画像ファイルだけを取り出す。
// 動画は対象外 (スクリーンショットの貼り付けが目的。動画は添付ボタンから)。
function imageFilesFromClipboard(dt: DataTransfer | null): File[] {
  if (!dt) return [];
  const out: File[] = [];
  for (const item of dt.items) {
    if (item.kind !== "file" || !item.type.startsWith("image/")) continue;
    const f = item.getAsFile();
    if (f) out.push(f);
  }
  return out;
}

class ModalController {
  private modal: HTMLElement;
  private form: HTMLFormElement;
  private body: HTMLTextAreaElement;
  private imageUrl: HTMLInputElement;
  private quoteId: HTMLInputElement;
  private imageGrid: HTMLElement;
  private preview: HTMLElement;
  private previewName: HTMLElement;
  private previewHandle: HTMLElement;
  private previewText: HTMLElement;
  private previewThumb: HTMLImageElement;
  private title: HTMLElement;
  private submit: HTMLButtonElement;
  private file: HTMLInputElement;
  private counter: HTMLElement;
  private progress: HTMLElement;
  private progressBar: HTMLElement;
  private close: HTMLButtonElement;
  private imageURLs: string[] = [];
  // 動画 (任意): 1 投稿に 1 本まで。アップ済 URL は cmVideoUrl の hidden に入れる。
  // 投稿成功時に posts.video_url / video_thumbnail として handler が保存する。
  private videoFile: HTMLInputElement | null;
  private videoUrl: HTMLInputElement | null;
  private videoThumb: HTMLInputElement | null;
  private videoPreview: HTMLElement | null;
  // 現況届バナー + 隠し input (なくても動作 = nullable で扱う)
  private genkyoId: HTMLInputElement | null;
  private genkyoBanner: HTMLElement | null;
  private genkyoTopicEl: HTMLElement | null;
  // 予約投稿 (schedule_at): cmScheduleBtn でパネルを開き datetime-local で時刻を選ぶ。
  // 使えるのはトップレベルの通常投稿だけ (返信 / 談話室 / 現況届けは server が拒否)。
  private scheduleWrap: HTMLElement | null;
  private scheduleBtn: HTMLButtonElement | null;
  private scheduleLabel: HTMLElement | null;
  private schedulePanel: HTMLElement | null;
  private scheduleAt: HTMLInputElement | null;
  private scheduleClear: HTMLButtonElement | null;
  // submit のラベルは mode (投稿 / 返信) と 予約の有無の掛け算で決まる。
  // mode 側の文言をここに覚えておき、予約が入っている間だけ「予約する」で上書きする。
  private baseSubmitLabel: string = "投稿する";

  constructor(modal: HTMLElement) {
    this.modal = modal;
    this.form = $<HTMLFormElement>(modal, "#cmForm")!;
    this.body = $<HTMLTextAreaElement>(modal, "#cmBody")!;
    this.imageUrl = $<HTMLInputElement>(modal, "#cmImageUrl")!;
    this.quoteId = $<HTMLInputElement>(modal, "#cmQuoteId")!;
    this.imageGrid = $<HTMLElement>(modal, "#cmImageGrid")!;
    this.preview = $<HTMLElement>(modal, "#cmPreview")!;
    this.previewName = $<HTMLElement>(modal, "#cmPreviewName")!;
    this.previewHandle = $<HTMLElement>(modal, "#cmPreviewHandle")!;
    this.previewText = $<HTMLElement>(modal, "#cmPreviewText")!;
    this.previewThumb = $<HTMLImageElement>(modal, "#cmPreviewThumb")!;
    this.title = $<HTMLElement>(modal, "#cmTitle")!;
    this.submit = $<HTMLButtonElement>(modal, "#cmSubmit")!;
    this.file = $<HTMLInputElement>(modal, "#cmFile")!;
    this.counter = $<HTMLElement>(modal, "#cmCounter")!;
    this.progress = $<HTMLElement>(modal, "#cmProgress")!;
    this.progressBar = $<HTMLElement>(modal, "#cmProgressBar")!;
    this.close = $<HTMLButtonElement>(modal, "#cmClose")!;
    this.genkyoId = $<HTMLInputElement>(modal, "#cmGenkyoId");
    this.genkyoBanner = $<HTMLElement>(modal, "#cmGenkyoBanner");
    this.genkyoTopicEl = $<HTMLElement>(modal, "#cmGenkyoTopic");
    this.videoFile = $<HTMLInputElement>(modal, "#cmVideoFile");
    this.videoUrl = $<HTMLInputElement>(modal, "#cmVideoUrl");
    this.videoThumb = $<HTMLInputElement>(modal, "#cmVideoThumb");
    this.videoPreview = $<HTMLElement>(modal, "#cmVideoPreview");
    this.scheduleWrap = $<HTMLElement>(modal, ".sched-wrap");
    this.scheduleBtn = $<HTMLButtonElement>(modal, "#cmScheduleBtn");
    this.scheduleLabel = $<HTMLElement>(modal, "#cmScheduleLabel");
    this.schedulePanel = $<HTMLElement>(modal, "#cmSchedulePanel");
    this.scheduleAt = $<HTMLInputElement>(modal, "#cmScheduleAt");
    this.scheduleClear = $<HTMLButtonElement>(modal, "#cmScheduleClear");

    this.close.addEventListener("click", () => this.hide());
    this.modal.addEventListener("click", (e) => { if (e.target === this.modal) this.hide(); });
    this.body.addEventListener("input", () => this.updateCounter());
    this.file.addEventListener("change", () => this.handleFileChange());
    // クリップボード貼り付け (PC のスクリーンショット用)。paste は bubbling するので
    // モーダルのルートに 1 つ付ければ textarea でもツールバーでも拾える。
    this.modal.addEventListener("paste", (e) => this.handlePaste(e));
    if (this.videoFile) this.videoFile.addEventListener("change", () => this.handleVideoChange());
    if (this.scheduleBtn) this.scheduleBtn.addEventListener("click", () => this.toggleSchedulePanel());
    if (this.scheduleAt) {
      this.scheduleAt.addEventListener("input", () => this.syncSchedule());
      this.scheduleAt.addEventListener("change", () => this.syncSchedule());
    }
    if (this.scheduleClear) this.scheduleClear.addEventListener("click", () => this.clearSchedule());
    // 予約パネルの外側クリックで閉じる + 談話室を選び直した後の予約可否の再評価。
    // (談話室ピッカーのハンドラは composer_modal.html の inline script 側にあり、
    //  document への delegate はその後の bubbling で届くので値は更新済み。)
    document.addEventListener("click", (e) => this.handleDocClick(e));
    this.form.addEventListener("submit", (e) => this.handleSubmit(e));
    document.addEventListener("keydown", (e) => {
      if (e.key === "Escape" && this.modal.hasAttribute("open")) this.hide();
    });
  }

  open(opts: ComposerOpts): void {
    if (opts.mode === "reply" && opts.postId) {
      this.form.action = u("/reply/" + opts.postId);
      this.title.textContent = "返信する";
      this.setSubmitLabel("返信する");
      this.quoteId.value = "";
      this.body.placeholder = "@" + (opts.preview?.handle || "") + " に返信";
    } else if (opts.mode === "quote" && opts.postId) {
      this.form.action = u("/post");
      this.title.textContent = "引用投稿";
      this.setSubmitLabel("投稿する");
      this.quoteId.value = String(opts.postId);
      this.body.placeholder = "コメントを追加 (任意)";
    } else {
      this.form.action = u("/post");
      this.title.textContent = "投稿する";
      this.setSubmitLabel("投稿する");
      this.quoteId.value = "";
      this.body.placeholder = "今どないしてまんねや?";
    }

    // 返信モードでは新規投稿専用のコントロール (現況 / 談話室 / ノート・ストーリー)
    // を非表示にする。代わりに「投げ銭で返信」(SuperThanks 切替) を reply の時だけ出す。
    const isReply = opts.mode === "reply";
    document.querySelectorAll<HTMLElement>("#composerModal .cm-newpost-only").forEach((el) => {
      el.hidden = isReply;
    });
    const tipBtn = document.getElementById("cmTipSwitchBtn");
    if (tipBtn) {
      tipBtn.hidden = !(isReply && opts.postId);
      if (isReply && opts.postId) {
        tipBtn.setAttribute("data-post-id", String(opts.postId));
        tipBtn.setAttribute("data-handle", opts.preview?.handle || "");
      } else {
        tipBtn.setAttribute("data-post-id", "");
        tipBtn.setAttribute("data-handle", "");
      }
    }

    if (opts.preview) {
      const p = opts.preview;
      this.preview.hidden = false;
      this.previewName.textContent = p.display_name || p.handle || "";
      this.previewHandle.textContent = "@" + (p.handle || "");
      this.previewText.textContent = p.body || "";
      const thumb = p.image_url || p.video_thumbnail || "";
      if (thumb) {
        this.previewThumb.src = thumb;
        this.previewThumb.hidden = false;
      } else {
        this.previewThumb.hidden = true;
        this.previewThumb.src = "";
      }
    } else {
      this.preview.hidden = true;
    }

    // 現況届コンテキスト
    if (this.genkyoId && this.genkyoBanner) {
      // 2026-09-23: 選んだお題はピッカーと同じ 1 行で見せる (composer_modal.html の cmSetGenkyo)。
      // 無い (古い断片) ときだけ従来どおり id と hidden を直に触る。
      const g = window as unknown as GenkyoSetter;
      if (opts.genkyoId && opts.genkyoId > 0) {
        if (typeof g.cmSetGenkyo === "function") {
          g.cmSetGenkyo({
            id: opts.genkyoId, topic: opts.genkyoTopic || "", genre: opts.genkyoGenre || "",
            host: opts.genkyoHost || "", remain: opts.genkyoRemain || "",
            ans: typeof opts.genkyoAnswers === "number" ? opts.genkyoAnswers : -1, mori: 0,
          });
        } else {
          this.genkyoId.value = String(opts.genkyoId);
          if (this.genkyoTopicEl) this.genkyoTopicEl.textContent = opts.genkyoTopic || "";
          this.genkyoBanner.hidden = false;
        }
      } else if (typeof g.cmClearGenkyo === "function") {
        g.cmClearGenkyo();
      } else {
        this.genkyoId.value = "";
        this.genkyoBanner.hidden = true;
      }
    }

    this.body.value = "";
    this.imageURLs = [];
    this.imageUrl.value = "";
    this.renderImageGrid();
    this.updateCounter();
    this.form.querySelectorAll<HTMLInputElement>('input[name="image_urls[]"]').forEach((el) => el.remove());
    // 動画もリセット
    this.clearVideo();
    // 同好談話室ピッカーもリセット (前回の選択が新しい mode に持ち越されないように)。
    // reply / quote モードでは danwa はそもそも使えないので確実にクリアしておく。
    // window globals は minifier の DCE で消えるので DOM 直叩きでリセット。
    this.resetDanwa();
    // 予約も同様にリセット (前回の予約時刻を新しい投稿に持ち越さない)。
    // genkyo / danwa / form.action が確定した後に呼ぶこと (可否をそこから判定する)。
    this.resetSchedule();

    this.modal.setAttribute("open", "");
    // キーボードを開いてもシート (特にツールバーと入力欄) が隠れないよう、
    // モーダルを visualViewport = キーボードを除いた見えている領域に追従させる。
    this.attachViewportSync();
    // iOS standalone PWA (ホーム画面アプリ) 対策:
    // composer-modal は display:none → flex で表示する。切り替えた直後は要素の
    // レイアウトが未確定で、その状態で focus() してもソフトキーボードが出ない。
    // 同期的にリフローを強制して textarea を可視化してから、ユーザージェスチャと
    // 同じ tick 内で focus する (iOS は「ジェスチャ内」かつ「可視要素」でないと
    // キーボードを出さない)。setTimeout/rAF を挟むとジェスチャが切れて出なくなる。
    void this.modal.offsetHeight;
    try {
      this.body.focus({ preventScroll: true });
    } catch (e) {
      this.body.focus();
    }
    // カーソルを末尾へ (返信編集の体験向上。失敗しても無視)。
    try {
      const n = this.body.value.length;
      this.body.setSelectionRange(n, n);
    } catch (e) {
      /* noop */
    }
  }

  hide(): void {
    this.modal.removeAttribute("open");
    this.detachViewportSync();
  }

  /** キーボード追従 (スマホのみ)。iOS はソフトキーボードでレイアウトビューポートが
   *  縮まないため、fixed inset:0 のモーダルは下 1/3 がキーボードの裏に沈む
   *  (返信シートのツールバー/入力欄が隠れる実機事故 2026-08-22)。モーダル自体を
   *  visualViewport の位置と高さに合わせれば、上寄せ/下寄せどちらのシートも
   *  常にキーボードの上に収まり、中身は .cm-body の内側スクロールで吸収される。 */
  private vvSync: (() => void) | null = null;

  private attachViewportSync(): void {
    const vv = window.visualViewport;
    if (!vv || !window.matchMedia("(max-width: 720px)").matches) return;
    const sync = (): void => {
      this.modal.style.top = vv.offsetTop + "px";
      this.modal.style.height = vv.height + "px";
      this.modal.style.bottom = "auto";
    };
    this.vvSync = sync;
    vv.addEventListener("resize", sync);
    vv.addEventListener("scroll", sync);
    sync();
  }

  private detachViewportSync(): void {
    const vv = window.visualViewport;
    if (this.vvSync && vv) {
      vv.removeEventListener("resize", this.vvSync);
      vv.removeEventListener("scroll", this.vvSync);
    }
    this.vvSync = null;
    this.modal.style.top = "";
    this.modal.style.height = "";
    this.modal.style.bottom = "";
  }

  private updateCounter(): void {
    const len = [...this.body.value].length;
    // 上限は maxlength 属性が正 (パルスモードで 30 に切り替わる。280 固定に
    // 戻すとパルス中のカウンタが嘘をつく)。警告のしきい値は上限に比例させる。
    const max = parseInt(this.body.getAttribute("maxlength") || "280", 10) || 280;
    const remain = max - len;
    const warnAt = max <= 60 ? 5 : 20;
    this.counter.textContent = String(remain);
    this.counter.style.color = remain < 0 ? "var(--accent-pink)" : remain < warnAt ? "var(--accent-gold)" : "";
  }

  /** パルスのピル (§13.4c) が写真つきの色 (紫→フクシア) と絵に切り替えるための印。 */
  private syncMediaClass(): void {
    const has = this.imageURLs.length > 0 || !!(this.videoUrl && this.videoUrl.value);
    this.modal.classList.toggle("cm-has-media", has);
  }

  private renderImageGrid(): void {
    this.syncMediaClass();
    this.imageGrid.innerHTML = "";
    this.imageURLs.forEach((url, i) => {
      const tile = document.createElement("div");
      tile.className = "cm-image-tile";
      const img = document.createElement("img");
      img.src = u("/img/400/" + url);
      const btn = document.createElement("button");
      btn.type = "button";
      btn.className = "cm-image-remove";
      btn.textContent = "×";
      btn.addEventListener("click", () => {
        this.imageURLs.splice(i, 1);
        this.syncHidden();
        this.renderImageGrid();
      });
      tile.appendChild(img);
      tile.appendChild(btn);
      this.imageGrid.appendChild(tile);
    });
    this.syncHidden();
  }

  private syncHidden(): void {
    this.imageUrl.value = this.imageURLs[0] || "";
    this.form.querySelectorAll<HTMLInputElement>('input[name="image_urls[]"]').forEach((el) => el.remove());
    for (let i = 1; i < this.imageURLs.length; i++) {
      const inp = document.createElement("input");
      inp.type = "hidden";
      inp.name = "image_urls[]";
      inp.value = this.imageURLs[i];
      this.form.appendChild(inp);
    }
  }

  private async handleFileChange(): Promise<void> {
    const files = Array.from(this.file.files || []);
    if (files.length === 0) return;
    try {
      await this.addImageFiles(files);
    } finally {
      // 同じファイルを選び直せるように毎回クリアする。
      this.file.value = "";
    }
  }

  // handlePaste はクリップボードに画像があればそれを添付する。
  // 画像が 1 枚も無い (テキストのみの) 貼り付けは何もせず素通りさせる。
  private handlePaste(e: ClipboardEvent): void {
    const files = imageFilesFromClipboard(e.clipboardData);
    if (files.length === 0) return;
    e.preventDefault();
    void this.addImageFiles(files);
  }

  // addImageFiles は 枚数検証 → /upload → プレビュー反映 までの共通経路。
  // ファイル選択と貼り付けの両方がここを通る (アップロード処理を二重に持たない)。
  private async addImageFiles(files: File[]): Promise<void> {
    const remaining = MAX_IMAGES - this.imageURLs.length;
    if (remaining <= 0) {
      showToast(`画像は ${MAX_IMAGES} 枚までです`, "warn");
      return;
    }
    this.submit.disabled = true;
    this.progress.hidden = false;
    try {
      for (const f of files.slice(0, remaining)) {
        const res = await upload(u("/upload"), "image", f, csrfToken(), (pct) => {
          this.progressBar.style.width = pct + "%";
        });
        if (!res.ok || !res.url) {
          showToast(res.error || "写真を送れませんでした", "error");
          continue;
        }
        this.imageURLs.push(res.url);
      }
      this.renderImageGrid();
    } catch (err) {
      showToast("通信できませんでした。電波の良いところでもう一度お試しください", "error");
    } finally {
      this.submit.disabled = false;
      this.progress.hidden = true;
      this.progressBar.style.width = "0%";
    }
  }

  private handleSubmit(e: Event): void {
    const hasVideo = !!(this.videoUrl && this.videoUrl.value);
    if (!this.body.value.trim() && this.imageURLs.length === 0 && !this.quoteId.value && !hasVideo) {
      e.preventDefault();
      this.body.focus();
      return;
    }
    // 連打防止: 弱回線で「ボタンを押しても反応がない → 連打」が原因の重複投稿
    // (post 1654-1657 で 4 件同一内容) 対策。submit イベントの後段で disable
    // することで、当該 form の native submit は通常通り進む。
    this.submit.disabled = true;
  }

  private async handleVideoChange(): Promise<void> {
    if (!this.videoFile || !this.videoUrl) return;
    const files = Array.from(this.videoFile.files || []);
    if (files.length === 0) return;
    // 動画は 1 本まで。既にあれば置換 (cmVideoUrl 上書き)。
    const f = files[0];
    this.submit.disabled = true;
    this.progress.hidden = false;
    try {
      const res = await uploadVideo(u("/upload/video"), f, csrfToken(), (pct) => {
        this.progressBar.style.width = pct + "%";
      });
      if (!res.ok || !res.url) {
        showToast(res.error || "動画を送れませんでした", "error");
        return;
      }
      this.videoUrl.value = res.url;
      if (this.videoThumb) this.videoThumb.value = res.thumb || "";
      this.renderVideoPreview(res.thumb || "");
      this.syncMediaClass();
    } catch (err) {
      showToast("通信できませんでした。電波の良いところでもう一度お試しください", "error");
    } finally {
      this.submit.disabled = false;
      this.progress.hidden = true;
      this.progressBar.style.width = "0%";
      this.videoFile.value = "";
    }
  }

  private renderVideoPreview(thumbPath: string): void {
    if (!this.videoPreview) return;
    this.videoPreview.innerHTML = "";
    const wrap = document.createElement("div");
    wrap.className = "cm-video-tile";
    if (thumbPath) {
      const img = document.createElement("img");
      img.src = u("/img/400/" + thumbPath);
      img.alt = "";
      wrap.appendChild(img);
    } else {
      // サムネが無い場合は単なるプレースホルダ
      const ph = document.createElement("div");
      ph.className = "cm-video-tile-noimg";
      ph.textContent = "🎬 動画";
      wrap.appendChild(ph);
    }
    const btn = document.createElement("button");
    btn.type = "button";
    btn.className = "cm-image-remove";
    btn.textContent = "×";
    btn.addEventListener("click", () => this.clearVideo());
    wrap.appendChild(btn);
    this.videoPreview.appendChild(wrap);
    this.videoPreview.hidden = false;
  }

  private clearVideo(): void {
    if (this.videoUrl) this.videoUrl.value = "";
    if (this.videoThumb) this.videoThumb.value = "";
    this.syncMediaClass();
    if (this.videoFile) this.videoFile.value = "";
    if (this.videoPreview) {
      this.videoPreview.innerHTML = "";
      this.videoPreview.hidden = true;
    }
  }

  // 同好談話室ピッカーの選択状態をリセットする (open() で毎回呼ぶ)。
  // composer_modal.html の inline script (cmToggleDanwaMenu) が管理する要素を DOM 直叩きで戻す。
  private resetDanwa(): void {
    const ids = ["cmDanwaRoomId"];
    ids.forEach((id) => {
      const el = document.getElementById(id) as HTMLInputElement | null;
      if (el) el.value = "";
    });
    const lbl = document.getElementById("cmDanwaPickLabel");
    if (lbl) lbl.textContent = "投稿先";
    const btn = document.getElementById("cmDanwaPickBtn");
    if (btn) { btn.classList.remove("active"); btn.dataset.room = ""; }
    const bnr = document.getElementById("cmDanwaBanner");
    if (bnr) bnr.hidden = true;
    const menu = document.getElementById("cmDanwaMenu");
    if (menu) menu.hidden = true;
  }

  // 予約時刻の現在値。disabled のときは「予約なし」扱い (実際に送信もされない)。
  private scheduleValue(): string {
    if (!this.scheduleAt || this.scheduleAt.disabled) return "";
    return this.scheduleAt.value;
  }

  // mode 側の submit ラベル (投稿 / 返信) を覚えて反映する。
  private setSubmitLabel(label: string): void {
    this.baseSubmitLabel = label;
    this.applySubmitLabel();
  }

  private applySubmitLabel(): void {
    this.submit.textContent = this.scheduleValue() ? "予約する" : this.baseSubmitLabel;
  }

  // 予約ボタンのラベル / active 表示と submit ラベルを現在値に合わせる。
  private syncSchedule(): void {
    const v = this.scheduleValue();
    if (this.scheduleLabel) this.scheduleLabel.textContent = v ? formatScheduleLabel(v) : "予約";
    if (this.scheduleBtn) this.scheduleBtn.classList.toggle("active", !!v);
    this.applySubmitLabel();
  }

  // 予約できる組合せか。返信 (form.action が /reply/...) / 談話室 / 現況届けは
  // server 側で拒否されるので UI からも消す。判定材料は全て DOM から読む
  // (open() の引数と、モーダルを開いた後の談話室選択を同じ式で捌くため)。
  private canSchedule(): boolean {
    if (/\/reply\//.test(this.form.action)) return false;
    if (this.genkyoId && this.genkyoId.value) return false;
    const danwa = document.getElementById("cmDanwaRoomId") as HTMLInputElement | null;
    if (danwa && danwa.value) return false;
    return true;
  }

  // 予約コントロールの可否を今の DOM から作り直す。使えない組合せでは値を捨てて
  // input を disabled にする (disabled な input は送信されないので server に届かない)。
  private refreshSchedule(): void {
    const ok = this.canSchedule();
    if (this.scheduleWrap) this.scheduleWrap.hidden = !ok;
    if (this.scheduleAt) {
      if (!ok) this.scheduleAt.value = "";
      this.scheduleAt.disabled = ok === false;
    }
    if (!ok && this.schedulePanel) this.schedulePanel.hidden = true;
    this.syncSchedule();
  }

  // open() ごとに呼ぶ: 予約時刻をクリアし、min / max を「いま」基準で貼り直す。
  private resetSchedule(): void {
    if (this.scheduleAt) this.scheduleAt.value = "";
    if (this.schedulePanel) this.schedulePanel.hidden = true;
    this.applyScheduleBounds();
    this.refreshSchedule();
  }

  // datetime-local の min / max。min は server の下限 (2 分先) にマージン 1 分を
  // 足した 3 分先: min ちょうどを選んで数十秒後に送ると now が進んで弾かれるため。
  // 値はローカル時刻の "YYYY-MM-DDTHH:MM" (localDateTimeValue 参照)。
  private applyScheduleBounds(): void {
    if (!this.scheduleAt) return;
    const now = Date.now();
    this.scheduleAt.min = localDateTimeValue(new Date(now + 3 * 60 * 1000));
    this.scheduleAt.max = localDateTimeValue(new Date(now + 60 * 24 * 60 * 60 * 1000));
  }

  private toggleSchedulePanel(): void {
    if (!this.schedulePanel) return;
    const opening = this.schedulePanel.hidden;
    // 開くたびに min / max を貼り直す (モーダルを開いたまま時間が経った場合の保険)。
    if (opening) this.applyScheduleBounds();
    this.schedulePanel.hidden = opening === false;
    if (opening && this.scheduleAt) {
      try {
        this.scheduleAt.focus({ preventScroll: true });
      } catch (e) {
        this.scheduleAt.focus();
      }
    }
  }

  private clearSchedule(): void {
    if (this.scheduleAt) this.scheduleAt.value = "";
    if (this.schedulePanel) this.schedulePanel.hidden = true;
    this.syncSchedule();
  }

  private handleDocClick(e: Event): void {
    const t = e.target instanceof Element ? e.target : null;
    if (!t) return;
    // 談話室の選択/解除は予約の可否を変える。入口はピッカー (.cm-danwa-wrap) と
    // バナーの × (.cm-inline-banner-clear) の 2 つ。
    if (t.closest(".cm-danwa-wrap") || t.closest(".cm-genkyo-wrap") || t.closest(".cm-inline-banner-clear")) {
      // 談話室メニューと予約パネルは同じ位置に開くので、片方を触ったら他方を閉じる
      // (逆方向は inline script の「メニュー外クリックで閉じる」が面倒を見る)。
      if (this.schedulePanel) this.schedulePanel.hidden = true;
      this.refreshSchedule();
      return;
    }
    if (this.schedulePanel && !this.schedulePanel.hidden && !t.closest(".sched-wrap")) {
      this.schedulePanel.hidden = true;
    }
  }
}

function upload(
  endpoint: string,
  field: string,
  file: File,
  csrf: string,
  onProgress: (pct: number) => void,
): Promise<UploadResponse> {
  return new Promise((resolve, reject) => {
    const fd = new FormData();
    fd.append(field, file);
    const xhr = new XMLHttpRequest();
    xhr.upload.addEventListener("progress", (e) => {
      if (e.lengthComputable) onProgress(Math.round((e.loaded / e.total) * 100));
    });
    xhr.addEventListener("load", () => {
      try { resolve(JSON.parse(xhr.responseText)); }
      catch { reject(new Error("invalid JSON response")); }
    });
    xhr.addEventListener("error", () => reject(new Error("network error")));
    xhr.open("POST", endpoint);
    xhr.setRequestHeader("X-CSRF-Token", csrf);
    xhr.send(fd);
  });
}

// uploadVideo は /upload/video に multipart で "video" フィールドを送る。
// upload_video.go は {ok, url, thumb} を返す (PHP 互換の {success,thumbnail} も併記)。
function uploadVideo(
  endpoint: string,
  file: File,
  csrf: string,
  onProgress: (pct: number) => void,
): Promise<UploadVideoResponse> {
  return new Promise((resolve, reject) => {
    const fd = new FormData();
    fd.append("video", file);
    const xhr = new XMLHttpRequest();
    xhr.upload.addEventListener("progress", (e) => {
      if (e.lengthComputable) onProgress(Math.round((e.loaded / e.total) * 100));
    });
    xhr.addEventListener("load", () => {
      try { resolve(JSON.parse(xhr.responseText)); }
      catch { reject(new Error("invalid JSON response")); }
    });
    xhr.addEventListener("error", () => reject(new Error("network error")));
    xhr.open("POST", endpoint);
    xhr.setRequestHeader("X-CSRF-Token", csrf);
    xhr.send(fd);
  });
}

function setupInlineComposer(form: HTMLFormElement): void {
  const fileInput = $<HTMLInputElement>(form, ".cm-file");
  const urlInput = $<HTMLInputElement>(form, ".cm-image-url");
  const preview = $<HTMLElement>(form, ".cm-preview");
  const progressWrap = $<HTMLElement>(form, ".cm-progress");
  const progressBar = $<HTMLElement>(form, ".cm-progress-bar");
  const submitBtn = $<HTMLButtonElement>(form, ".cm-submit");
  const body = $<HTMLTextAreaElement>(form, ".cm-body");
  const counter = $<HTMLElement>(form, ".cm-counter");
  if (!fileInput || !urlInput || !preview || !submitBtn || !body) return;

  fileInput.multiple = true;
  const imageURLs: string[] = [];

  const updateCounter = () => {
    if (!counter) return;
    // 上限は maxlength 属性が正 (280 ハードコードにしない)。
    // ※ インラインコンポーザには現状カウンタ要素が無い面もある — その場合この
    //   関数は counter ガードで何もしない。要素を足せばそのまま 30/280 に追従する
    const max = parseInt(body.getAttribute("maxlength") || "280", 10) || 280;
    const remain = max - [...body.value].length;
    const warnAt = max <= 60 ? 5 : 20;
    counter.textContent = String(remain);
    counter.style.color = remain < 0 ? "var(--accent-pink)" : remain < warnAt ? "var(--accent-gold)" : "";
  };
  body.addEventListener("input", updateCounter);
  updateCounter();

  const renderPreview = () => {
    preview.innerHTML = "";
    imageURLs.forEach((url, i) => {
      const tile = document.createElement("div");
      tile.className = "cm-image-tile";
      const img = document.createElement("img");
      img.src = u("/img/400/" + url);
      const btn = document.createElement("button");
      btn.type = "button";
      btn.className = "cm-image-remove";
      btn.textContent = "×";
      btn.addEventListener("click", () => {
        imageURLs.splice(i, 1);
        renderPreview();
      });
      tile.appendChild(img);
      tile.appendChild(btn);
      preview.appendChild(tile);
    });
    preview.hidden = imageURLs.length === 0;
    urlInput.value = imageURLs[0] || "";
    form.querySelectorAll<HTMLInputElement>('input[name="image_urls[]"]').forEach((el) => el.remove());
    for (let i = 1; i < imageURLs.length; i++) {
      const inp = document.createElement("input");
      inp.type = "hidden";
      inp.name = "image_urls[]";
      inp.value = imageURLs[i];
      form.appendChild(inp);
    }
  };

  form.addEventListener("submit", (e) => {
    const hasQuote = form.querySelector<HTMLInputElement>('input[name="quote_post_id"]')?.value;
    const hasVideo = form.querySelector<HTMLInputElement>(".cm-video-url")?.value;
    if (!body.value.trim() && imageURLs.length === 0 && !hasQuote && !hasVideo) {
      e.preventDefault();
      body.focus();
      return;
    }
    // 連打防止: submit イベントの後段で disable することで当該 form の native
    // submit は通常どおり進みつつ、弱回線での連打二重投稿を防ぐ (モーダル
    // handleSubmit と同じ流儀)。
    submitBtn.disabled = true;
  });

  // addImageFiles は 枚数検証 → /upload → プレビュー反映 までの共通経路。
  // ファイル選択と貼り付けの両方がここを通る (モーダル側と同じ流儀)。
  const addImageFiles = async (files: File[]) => {
    const remaining = MAX_IMAGES - imageURLs.length;
    if (remaining <= 0) {
      showToast(`画像は ${MAX_IMAGES} 枚までです`, "warn");
      return;
    }
    submitBtn.disabled = true;
    if (progressWrap) progressWrap.hidden = false;
    try {
      for (const f of files.slice(0, remaining)) {
        const res = await upload(u("/upload"), "image", f, csrfToken(), (pct) => {
          if (progressBar) progressBar.style.width = pct + "%";
        });
        if (!res.ok || !res.url) {
          showToast(res.error || "写真を送れませんでした", "error");
          continue;
        }
        imageURLs.push(res.url);
      }
      renderPreview();
    } catch (err) {
      showToast("通信できませんでした。電波の良いところでもう一度お試しください", "error");
    } finally {
      submitBtn.disabled = false;
      if (progressWrap) progressWrap.hidden = true;
      if (progressBar) progressBar.style.width = "0%";
    }
  };

  fileInput.addEventListener("change", async () => {
    const files = Array.from(fileInput.files || []);
    if (files.length === 0) return;
    try {
      await addImageFiles(files);
    } finally {
      // 同じファイルを選び直せるように毎回クリアする。
      fileInput.value = "";
    }
  });

  // クリップボード貼り付け (PC のスクリーンショット用)。paste は bubbling するので
  // form ルートに 1 つ付ければ textarea でも他のコントロールでも拾える。
  form.addEventListener("paste", (e) => {
    const files = imageFilesFromClipboard(e.clipboardData);
    if (files.length === 0) return;
    e.preventDefault();
    void addImageFiles(files);
  });
}

let controller: ModalController | null = null;
function ensureController(): ModalController | null {
  if (controller) return controller;
  const modal = document.getElementById("composerModal");
  if (!modal) return null;
  controller = new ModalController(modal);
  return controller;
}

function handleTriggerClick(e: Event): void {
  const t = (e.target as Element)?.closest?.("[data-composer-trigger]") as HTMLElement | null;
  if (!t) return;
  const raw = t.getAttribute("data-composer-trigger");
  if (!raw) return;
  let opts: ComposerOpts;
  try { opts = JSON.parse(raw); } catch { return; }
  e.preventDefault();
  e.stopPropagation();
  const c = ensureController();
  if (!c) {
    if (t.tagName === "A" && (t as HTMLAnchorElement).href) {
      window.location.href = (t as HTMLAnchorElement).href;
    }
    return;
  }
  c.open(opts);
}

export function initComposers(): void {
  const queued = Array.isArray(window.__cmQueue) ? window.__cmQueue.slice() : [];
  window.__cmQueue = undefined;
  window.openComposerModal = (opts: ComposerOpts) => {
    const c = ensureController();
    if (c) c.open(opts);
  };
  window.closeComposerModal = () => controller?.hide();
  document.addEventListener("click", handleTriggerClick, true);
  document.querySelectorAll<HTMLFormElement>("form.composer-inline").forEach(setupInlineComposer);
  queued.forEach((opts) => window.openComposerModal!(opts));
  openFromComposeQuery();
  document.addEventListener("mp:navigated", openFromComposeQuery);
}

/**
 * `?compose=1` で開かれたらコンポーザを開く (2026-09-16 監査 P-016)。
 * 殻の「投稿して稼ぐ」と、いいねの 402 トーストの「投稿して稼ぐ」は
 * data-composer-trigger でその場で開くが、JS が効く前に押された / 別タブで開かれた
 * ときの受け皿。?genkyo_id= (base.html) と同じく、開いたら URL から消す
 * (戻る・再読み込みで何度も開かない)。⚠ 刻印 (history.state.mp) を落とさないよう
 * MoriokaHistory 経由で置換する。
 */
function openFromComposeQuery(): void {
  let url: URL;
  try {
    url = new URL(window.location.href);
  } catch {
    return;
  }
  if (url.searchParams.get("compose") !== "1") return;
  url.searchParams.delete("compose");
  const rest = url.searchParams.toString();
  const next = url.pathname + (rest ? "?" + rest : "") + url.hash;
  const H = (window as unknown as { MoriokaHistory?: { replaceState(p: unknown, u: string): void } }).MoriokaHistory;
  try {
    if (H && typeof H.replaceState === "function") H.replaceState(null, next);
    else window.history.replaceState(window.history.state, "", next);
  } catch {
    /* URL を消せなくても開くことは開く */
  }
  const c = ensureController();
  if (c) c.open({ mode: "new" });
}
